import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCheck, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function BulkCloseBar({ selected, selectable, onSelectAll, onClear, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = selected.size;

  const closeAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/bulk-close", { ticket_ids: [...selected], reason: note });
      const parts = [];
      if (data.closed.length) parts.push(`${data.closed.length} closed`);
      if (data.approval_requested.length) parts.push(`${data.approval_requested.length} sent for approval`);
      if (data.skipped.length) parts.push(`${data.skipped.length} skipped`);
      toast[data.closed.length || data.approval_requested.length ? "success" : "info"](parts.join(" · ") || "Nothing to do", {
        description: data.skipped.length ? data.skipped.map((s) => `${s.number || "?"}: ${s.reason}`).join(", ") : undefined,
      });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      selected.forEach((id) => qc.invalidateQueries({ queryKey: ["ticket", id] }));
      setOpen(false); setNote("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 border-b border-primary/40 bg-primary/10 px-3 py-2 text-xs" data-testid="bulk-bar">
      <span className="font-semibold tabular-nums" data-testid="bulk-selected-count">{n} selected</span>
      <button className="text-primary hover:underline" onClick={onSelectAll} data-testid="bulk-select-all-btn">Select all {selectable} open</button>
      <span className="ml-auto flex items-center gap-1.5">
        <Button size="sm" className="h-7 text-xs" disabled={!n} onClick={() => setOpen(true)} data-testid="bulk-close-btn"><CheckCheck className="mr-1 h-3.5 w-3.5" /> Close {n || ""}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClear} data-testid="bulk-cancel-btn"><X className="mr-1 h-3.5 w-3.5" /> Done</Button>
      </span>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="bulk-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Close {n} ticket{n === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>Tickets under a closure-approval rule are sent for approval instead of closing. Closed tickets stay in the archive and can be reopened.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional closing note (added to every ticket's history)" className="min-h-[70px] text-sm" data-testid="bulk-close-note" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-close-cancel-btn">Keep open</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); closeAll(); }} data-testid="bulk-close-confirm-btn">{busy ? "Closing…" : `Close ${n} ticket${n === 1 ? "" : "s"}`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
